<template>
  <div class="hr-page" :class="{ 'nav-collapsed': isNavCollapsed }">
    <Navigation @nav-toggle="handleNavToggle" />

    <div class="dashboard-content">
      <div class="page-container">
        <!-- Modern page header -->
        <div class="modern-page-header" :class="{ 'modern-page-header--institutional': headerTone === 'institutional' }">
          <div class="header-content">
            <div class="title-section">
              <h1 class="dashboard-title">{{ title }}</h1>
              <p v-if="subtitle" class="dashboard-subtitle">
                <span class="icon" v-if="icon">{{ icon }}</span>
                {{ subtitle }}
              </p>
            </div>
            <div class="header-actions">
              <slot name="header-actions"></slot>
            </div>
          </div>
          <div v-if="breadcrumbItems.length" class="breadcrumb-section">
            <div class="breadcrumb">
              <span class="breadcrumb-item" @click="goHome">
              </span>
              <template v-for="(item, idx) in breadcrumbItems" :key="'bc-' + idx">
                <span v-if="idx > 0" class="breadcrumb-separator">/</span>
                <span
                  class="breadcrumb-item"
                  :class="{ 'breadcrumb-current': idx === breadcrumbItems.length - 1 }"
                  @click="item.route ? $router.push(item.route) : null"
                >
                  {{ item.label }}
                </span>
              </template>
            </div>
          </div>
        </div>

        <!-- Main content slot -->
        <div class="hr-page-content">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/Navigation.vue";

export default {
  name: "HrPageLayout",
  components: { Navigation },
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    icon: { type: String, default: "" },
    breadcrumbItems: {
      type: Array,
      default: () => []
    },
    /** Visual tone for the top page header card. 'institutional' uses formal government-style accents. */
    headerTone: {
      type: String,
      default: "default",
      validator: (v) => ["default", "institutional"].includes(v)
    }
  },
  data() {
    return {
      isNavCollapsed: false
    };
  },
  methods: {
    handleNavToggle(isCollapsed) {
      this.isNavCollapsed = isCollapsed;
    },
    goHome() {
      this.$router.push("/dashboard");
    }
  }
};
</script>

<style scoped>
/* Pure Vue layout: no Bootstrap. Content offset must match Navigation width (280px / 70px collapsed). */
.hr-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  box-sizing: border-box;
}

.hr-page.nav-collapsed .dashboard-content {
  margin-left: 70px;
}

.dashboard-content {
  margin-left: 280px;
  padding: 20px;
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  /* Ensure content never goes under the fixed nav */
  position: relative;
  z-index: 1;
}

.page-container {
  max-width: 1600px;
  margin: 0 auto;
}

.modern-page-header {
  background: #ffffff;
  border-radius: 20px;
  padding: 25px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  overflow: visible; /* allow dropdowns in header-actions to show below */
}

.modern-page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #059669 0%, #16a34a 100%);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 15px;
}

.title-section {
  flex: 1;
  min-width: 300px;
}

.dashboard-title {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.2;
}

.dashboard-subtitle {
  color: #64748b;
  font-size: 15px;
  margin: 8px 0 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.breadcrumb-section {
  padding-top: 15px;
  border-top: 1px solid #e2e8f0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 14px;
  font-weight: 500;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #475569;
  cursor: pointer;
  transition: color 0.2s;
}

.breadcrumb-item:hover {
  color: #059669;
}

.breadcrumb-current {
  color: #94a3b8;
  font-weight: 600;
}

/* Formal / public-sector page header (optional per page) */
.modern-page-header--institutional {
  border-radius: 4px;
  border: 1px solid #c5d4e0;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  padding: 22px 28px;
}
.modern-page-header--institutional::before {
  height: 5px;
  background: linear-gradient(90deg, #0c4a6e 0%, #155e75 45%, #b45309 100%);
}
.modern-page-header--institutional .dashboard-title {
  font-weight: 700;
  font-size: 26px;
  letter-spacing: 0.02em;
  color: #0f172a;
}
.modern-page-header--institutional .dashboard-subtitle {
  color: #475569;
  font-size: 14px;
  line-height: 1.55;
  margin-top: 10px;
  border-left: 3px solid #0c4a6e;
  padding-left: 14px;
}
.modern-page-header--institutional .dashboard-subtitle .icon {
  display: none;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

.hr-page-content {
  margin-bottom: 30px;
}

@media (max-width: 1024px) {
  .dashboard-content {
    margin-left: 70px;
  }
}

@media (max-width: 768px) {
  .dashboard-content {
    margin-left: 0;
    padding: 15px;
  }
  .hr-page.nav-collapsed .dashboard-content {
    margin-left: 0;
  }
  .dashboard-title {
    font-size: 22px;
  }
}
</style>
